// HTTP surface of the MaterialRegistry feature (lureo-backend#257). What a write
// tells the renderer and the undo stack lives in materialSync.

import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import {
  MaterialInstanceRow,
  MaterialPreset,
  ShaderLook,
  materialInstanceFromDto,
  materialPresetFromDto,
  shaderLookFromDto,
} from './types';

import type { WriteScope } from '@/modules/undoRedo';
export interface MaterialWriteItem {
  assetInstanceId: string;
  materialTypeId?: string;
  shaderCodeName?: string;
  targetNodeName?: string;
  targetNodeIndex?: number;
  unityProperties?: Record<string, unknown> | null;
  webProperties?: Record<string, unknown> | null;
  webxrProperties?: Record<string, unknown> | null;
  swiftProperties?: Record<string, unknown> | null;
}

export async function fetchShaderLibrary(): Promise<ShaderLook[]> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/shader-looks', { params: { query: page } }));
  if (error) throw error;
  return data.map(shaderLookFromDto);
}

export async function fetchMaterialPresets(): Promise<MaterialPreset[]> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/material-types', { params: { query: page } }));
  if (error) throw error;
  return data.map(materialPresetFromDto);
}

export interface MaterialPresetWriteItem {
  displayName?: string;
  shaderCodeName?: string;
  unityProperties?: Record<string, unknown> | null;
  webProperties?: Record<string, unknown> | null;
  webxrProperties?: Record<string, unknown> | null;
  swiftProperties?: Record<string, unknown> | null;
  assetTypeId?: string;
  /** Requires assetTypeId; an empty array on patch clears the mapping. */
  targetNodeNames?: string[];
}

// The createMaterialType / updateMaterialType body for a preset write item
const presetBody = ({ assetTypeId, ...rest }: MaterialPresetWriteItem) => ({ ...rest, assetTypeUniqueId: assetTypeId });

// createMaterialType; the client id makes a repeat answer with the preset the first one made
export async function createMaterialPreset(item: MaterialPresetWriteItem, scope?: WriteScope): Promise<MaterialPreset> {
  const { data, error } = await toResult(v1.POST('/api/v1/material-types', { body: { uniqueId: crypto.randomUUID(), ...presetBody(item) }, headers: scope?.headers }));
  if (error) throw error;
  return materialPresetFromDto(data);
}

// updateMaterialType; an empty targetNodeNames clears the mapping
export async function patchMaterialPreset(materialTypeId: string, item: MaterialPresetWriteItem, scope?: WriteScope): Promise<MaterialPreset> {
  const { data, error } = await toResult(v1.PATCH('/api/v1/material-types/{materialTypeUniqueId}', {
    params: { path: { materialTypeUniqueId: materialTypeId } },
    body: presetBody(item),
    headers: scope?.headers,
  }));
  if (error) throw error;
  return materialPresetFromDto(data);
}

export async function deleteMaterialPreset(materialTypeId: string, scope?: WriteScope): Promise<void> {
  const { error } = await toResult(v1.DELETE('/api/v1/material-types/{materialTypeUniqueId}', { params: { path: { materialTypeUniqueId: materialTypeId } }, headers: scope?.headers }));
  if (error) throw error;
}

// listMaterialInstances; an asset instance already gone has no materials
export async function fetchAssetMaterials(assetInstanceId: string): Promise<MaterialInstanceRow[]> {
  const { data, error } = await listAll(page => v1.GET('/api/v1/material-instances', {
    params: { query: { assetInstanceUniqueId: [assetInstanceId], ...page } },
  }));
  if (error) {
    if (error.status === 404) return [];
    throw error;
  }
  return data.map(materialInstanceFromDto);
}

// upsertMaterialInstances: all or nothing, each replacing the material already on its node
export async function upsertMaterials(items: MaterialWriteItem[], scope?: WriteScope): Promise<MaterialInstanceRow[]> {
  const body = {
    items: items.map(({ assetInstanceId, materialTypeId, ...rest }) => ({ ...rest, assetInstanceUniqueId: assetInstanceId, materialTypeUniqueId: materialTypeId })),
  };
  const { data, error } = await toResult(v1.POST('/api/v1/material-instances/batch', { body, headers: scope?.headers }));
  if (error) throw error;
  return data.map(materialInstanceFromDto);
}

// updateMaterialInstance: each platform's overrides are replaced, so the change is merged into the row's own
export async function patchMaterial(row: MaterialInstanceRow, changed: Record<string, unknown>, scope?: WriteScope): Promise<MaterialInstanceRow> {
  const merge = (existing: Record<string, unknown> | null) => ({ ...(existing ?? {}), ...changed });
  const { data, error } = await toResult(v1.PATCH('/api/v1/material-instances/{uniqueId}', {
    params: { path: { uniqueId: row.materialInstanceId } },
    body: {
      unityProperties: merge(row.unityProperties),
      webProperties: merge(row.webProperties),
      webxrProperties: merge(row.webxrProperties),
      swiftProperties: merge(row.swiftProperties),
    },
    headers: scope?.headers,
  }));
  if (error) throw error;
  return materialInstanceFromDto(data);
}

// deleteMaterialInstance; one already gone is deleted
export async function deleteMaterial(materialInstanceId: string, scope?: WriteScope): Promise<void> {
  const { error } = await toResult(v1.DELETE('/api/v1/material-instances/{uniqueId}', { params: { path: { uniqueId: materialInstanceId } }, headers: scope?.headers }));
  if (error && error.status !== 404) throw error;
}

/** Lookdev push replaces the shader row's web_properties column with a complete platform snapshot. */
export async function pushShaderWebProperties(
  shaderLibraryId: string,
  properties: Record<string, unknown>
): Promise<void> {
  // Staff only: anyone else's push is refused (403)
  const { error } = await toResult(v1.PUT('/api/v1/shader-looks/{shaderLookUniqueId}/properties/{platform}', {
    params: { path: { shaderLookUniqueId: shaderLibraryId, platform: 'web' } },
    body: properties,
  }));
  if (error) throw error;
}

export const MATERIAL_ROWS_CHANGED = 'materialRowsChanged';
